/**
 * adminLinkResolver.js
 *
 * Pure core of the admin/services/app Universal & App Links deep-linking
 * feature: turns an incoming `https://` URL into `{mode, screen, params}`,
 * and turns that (plus what the signed-in user/build is allowed to do) into
 * a navigation decision.
 *
 * This file is intentionally dependency-free (no `react-native`, no
 * `@react-navigation/*`, no app helpers that touch AsyncStorage/native
 * modules) so it can be unit tested with plain Jest — see
 * `app/tests/adminLinkResolver.test.js`. The RN-touching side (actually
 * calling the OS `Linking` API, checking the signed-in user's roles,
 * switching `userAppMode` and imperatively navigating) lives in the sibling
 * file `adminLinkNavigation.js`, which imports the pure functions below.
 *
 * ---------------------------------------------------------------------------
 * Why `getStateFromPath` always returns `undefined`
 * ---------------------------------------------------------------------------
 * React Navigation's `linking.getStateFromPath(path, options)` only ever
 * receives the URL's PATH — the matched `prefixes` entry (scheme + host) is
 * stripped before this function is called, and is not passed in any other
 * argument. `https://admin.happysolutions.hu/crm` and
 * `https://services.happysolutions.hu/crm` both arrive as the identical
 * call `getStateFromPath('/crm', options)` — there is no way to tell THIS
 * function which host matched, and our routing fundamentally depends on the
 * host (services/app -> always Sales home; admin -> mapped screen, gated by
 * management access). On top of that, `getStateFromPath` must return
 * synchronously, while our routing needs an async permission check
 * (`managerRoles.getSwitchableAppModesForUser`) before it's safe to
 * navigate.
 *
 * So the REAL work happens in `adminLinkNavigation.js`'s custom
 * `getInitialURL` / `subscribe` implementations, which DO see the full raw
 * URL (host included) and can run async code before imperatively navigating
 * via `navigationRef` — the same pattern the app's existing
 * `helpers/widgets/widgetDeepLink.js` already uses for `happyapp://widget/`
 * links. `getStateFromPath` here is kept as a real, wired-in export (so the
 * `linking` config is complete and self-documenting) but is a deliberate
 * no-op: returning `undefined` tells React Navigation "no state change from
 * me", which is correct since the actual navigation already happened (or
 * will happen) imperatively.
 */

import {matchAdminPath, parseQuery} from './adminPathMapping';

export const ADMIN_HOST = 'admin.happysolutions.hu';
// Legacy brand host — same admin web app (Cloudflare currently 301s it to
// ADMIN_HOST); links on it resolve exactly like ADMIN_HOST links.
export const LEGACY_ADMIN_HOST = 'admin.happygastro.hu';
export const SERVICES_HOST = 'services.happysolutions.hu';
export const APP_HOST = 'app.happysolutions.hu';

// Existing custom URL schemes from app.json's Android intentFilters (and the
// Expo `scheme` field, which is included in this same list). These are added
// to `prefixes` per spec, but this resolver only ever ACTS on the three
// https hosts above — see `resolveAdminUrl`. Anything on a custom scheme
// (including `happyapp://widget/...`) resolves to `null` here and is left
// entirely to the existing `helpers/widgets/widgetDeepLink.js` handler.
export const CUSTOM_SCHEMES = [
    'happyservices',
    'happy-services',
    'happy-app',
    'happyapp',
    'happy-pos',
    'happypos',
    'happyrestaurant',
    'happy-restaurant',
    'happygastro',
    'happy-gastro',
    'happy',
];

export const ADMIN_LINKING_PREFIXES = [
    `https://${ADMIN_HOST}`,
    `https://${LEGACY_ADMIN_HOST}`,
    `https://${SERVICES_HOST}`,
    `https://${APP_HOST}`,
    ...CUSTOM_SCHEMES.map((scheme) => `${scheme}://`),
];

/**
 * Minimal, dependency-free URL parser. Deliberately NOT using the `URL`
 * global — Hermes/JSC in React Native do not reliably provide it without a
 * polyfill (the codebase doesn't install one; `widgetDeepLink.js` also
 * parses URLs by hand for the same reason), and a Jest test running under
 * Node's `URL` would pass while the on-device code crashed.
 * @param {string} url
 * @returns {{scheme: string, host: string, path: string}|null}
 */
export const parseUrl = (url) => {
    if (typeof url !== 'string' || !url) return null;
    const match = /^([a-zA-Z][a-zA-Z0-9+.-]*):\/\/([^/?#]*)([^?#]*)/.exec(url);
    if (!match) return null;
    const [, scheme, host, rawPath] = match;
    return {scheme: scheme.toLowerCase(), host: host.toLowerCase(), path: rawPath || '/'};
};

/**
 * Unwrap the admin web app's "open in app" hand-off link,
 * `<custom-scheme>://open?url=<encoded https URL>` (built by the admin repo's
 * `src/pwa/openInAppPrompt.js` — a plain https link to the page the browser
 * is already on can never leave the browser, so the admin page launches the
 * app through its custom scheme instead and carries the full URL along).
 * @param {string} url
 * @returns {string|null} the wrapped https URL, or null when `url` is not a hand-off link.
 */
export const unwrapOpenInAppUrl = (url) => {
    if (typeof url !== 'string') return null;
    const match = /^([a-zA-Z][a-zA-Z0-9+.-]*):\/\/open\/?\?(.*)$/.exec(url);
    if (!match || !CUSTOM_SCHEMES.includes(match[1].toLowerCase())) return null;
    const pair = match[2].split('&').find((part) => part.startsWith('url='));
    if (!pair) return null;
    try {
        const inner = decodeURIComponent(pair.slice(4));
        return /^https:\/\//i.test(inner) ? inner : null;
    } catch (_error) {
        return null;
    }
};

/**
 * A web-only page on one of our own https hosts (`/meet/p/:code` guest page,
 * `/meet/join` room) — `resolveAdminUrl` deliberately returns null for these,
 * but Android App Links still hand them to the app (host-wide filter), so the
 * caller shows them in an in-app browser instead of dropping the tap.
 * @param {string} url
 * @returns {string|null} the https URL to show on the web, or null.
 */
export const getOwnWebOnlyUrl = (url) => {
    const target = unwrapOpenInAppUrl(url) || url;
    const parsed = parseUrl(target);
    if (!parsed || parsed.scheme !== 'https') return null;
    if (![ADMIN_HOST, LEGACY_ADMIN_HOST].includes(parsed.host)) return null;
    const match = matchAdminPath(parsed.path, parseQuery(target));

    return match && !match.screen ? target : null;
};

/**
 * Resolve a full URL to `{mode, screen, params}`, per the task's mapping:
 *   - `services.happysolutions.hu` / `app.happysolutions.hu`, any path
 *     -> `{mode: 'sales', screen: 'HomeScreen'}`.
 *   - `admin.happysolutions.hu` -> `{mode: 'manager', screen: <mapped>}`,
 *     any admin path without a native screen -> `{mode: 'any', screen:
 *     'AdminWebScreen', params: {url}}` (the authenticated admin page in-app).
 *     Mode-independent screens (Team Chat, meetings) -> `{mode: 'any', ...}`;
 *     web-only admin pages (`/meet/join`, `/meet/p/:code`) -> `null`.
 *   - `<custom-scheme>://open?url=<encoded>` -> the wrapped URL, resolved as above.
 *   - anything else (unrecognized host, or a non-https custom scheme such as
 *     the widget links) -> `null` — not this resolver's concern.
 * @param {string} url
 * @returns {{mode: 'sales'|'manager'|'any', screen: string, params: object|undefined}|null}
 */
export const resolveAdminUrl = (url) => {
    const target = unwrapOpenInAppUrl(url) || url;
    const parsed = parseUrl(target);
    if (!parsed || parsed.scheme !== 'https') return null;

    if (parsed.host === SERVICES_HOST || parsed.host === APP_HOST) {
        return {mode: 'sales', screen: 'HomeScreen', params: undefined};
    }

    if (parsed.host === ADMIN_HOST || parsed.host === LEGACY_ADMIN_HOST) {
        // The query rides along: some admin links carry the record id there
        // (`/mail/inbox?message=<id>`, `/chat/<id>?message=<id>`).
        const match = matchAdminPath(parsed.path, parseQuery(target));
        if (match && !match.screen) return null;
        if (match) {
            return {mode: match.anyMode ? 'any' : 'manager', screen: match.screen, params: match.params};
        }
        // No native screen: open the admin page itself, signed in, inside the
        // app (AdminWebScreen). The admin web enforces its own permissions, so
        // no mode switch / management gate.
        return {mode: 'any', screen: 'AdminWebScreen', params: {url: target}};
    }

    return null;
};

/**
 * Decide what to do about a resolved target, given what the current
 * build/user is actually allowed to do. Pure — takes plain booleans instead
 * of touching roles/storage/variant helpers itself, so the access-deny path
 * is trivially testable without mocking React Native.
 *
 * @param {{mode: 'sales'|'manager'|'any', screen: string, params: object|undefined}|null} resolved
 * @param {object} capabilities
 * @param {boolean} [capabilities.salesModuleEnabled] Whether this build variant bundles the Sales module (default true).
 * @param {boolean} [capabilities.managerAllowed] Whether the signed-in user currently has management access.
 * @returns {{action: 'navigate', mode: 'sales'|'manager'|'any', screen: string, params: object|undefined}
 *         | {action: 'landing'}
 *         | {action: 'none'}}
 */
export const decideAdminNavigation = (resolved, {salesModuleEnabled = true, managerAllowed = false} = {}) => {
    if (!resolved) return {action: 'none'};

    if (resolved.mode === 'sales') {
        // "Always open the app in SALES module home" — no access gating,
        // only a build-capability guard (a management/service/warehouse-only
        // variant has no Sales module to open).
        if (!salesModuleEnabled) return {action: 'none'};
        return {action: 'navigate', mode: 'sales', screen: resolved.screen, params: resolved.params};
    }

    if (resolved.mode === 'any') {
        // Available in every app mode — the screen does its own access check.
        return {action: 'navigate', mode: 'any', screen: resolved.screen, params: resolved.params};
    }

    if (resolved.mode === 'manager') {
        if (!managerAllowed) {
            // Installed, but the signed-in user has no management access ->
            // the app's own main/landing page (native OS fallback already
            // handled "not installed at all" before we're ever reached).
            return {action: 'landing'};
        }
        return {action: 'navigate', mode: 'manager', screen: resolved.screen, params: resolved.params};
    }

    return {action: 'none'};
};

/**
 * React Navigation `linking.getStateFromPath` implementation. Always
 * returns `undefined` — see the file-level doc comment for why. Kept as a
 * real export (rather than omitted) so the `linking` config passed to
 * `NavigationContainer` is complete and matches what the task asked for.
 * @returns {undefined}
 */
export const getStateFromPath = () => undefined;

export default resolveAdminUrl;
